import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { selectGameConfig } from "src/store/common"
import { selectGame, selectValidUnitsForPlayer, selectViewerId } from "src/store/game"
import { cellDisplayName } from "src/utils/maps"
import { tunnelOrdersAt } from "src/utils/cellActions"
import { playerFacingError } from "src/utils/errorMessages"
import ModalService from "src/modals"

import Modal from "src/components/modals/Modal"
import UnitPicker, { takesText } from "src/components/UnitPicker"

import "./AttackModal.css"
import "./TunnelModal.css"

/* The tunnel network (owner, 2026-09-29; the Red Nydus's jump until then). One sheet, two modes,
   always for one city of yours with a tunnel — the one the order was given on:

   - `mode: "load"` — ENTRAR: who goes IN from this garrison, for one action (owner, 2026-09-30).
     Capped by this city's tunnel level (the entry) and by the room left in the network (the
     tunnel program's level), never from a city in a fight.
   - `mode: "unload"` — SALIR: who comes OUT of the network into this garrison, for one action.

   It is the march order's picker (`UnitPicker`) in the march order's frame — `.march-head`,
   `.march-foot` — so the sliders, the typed counts, Tab and Space behave as they do there. The
   refusals are the map bar's own (`tunnelOrdersAt` → `cellActions`): the confirm is disabled and
   says why. The server is the gate for all of it (`use_cases/tunnel.py`). */

/* The sheet's limit on one unit type: what stands there, and — loading — what is left of the
   load's capacity once the other types are counted. */
export const tunnelMaxFor = ({ type, available, troops, capacity }) => {
  if (capacity == null) return available
  const others = Object.entries(troops).reduce((sum, [other, number]) => (other === type ? sum : sum + (Number(number) || 0)), 0)
  return Math.max(0, Math.min(available, capacity - others))
}

/* A share of every stack, in order, until the load is full. */
export const tunnelFill = ({ pool, fraction, capacity }) => {
  let room = capacity ?? Infinity
  return Object.fromEntries(
    pool.map(({ type, available }) => {
      const number = Math.min(Math.floor(available * fraction), room)
      room -= number
      return [type, number]
    })
  )
}

const TunnelModal = ({ city, mode = "unload" }) => {
  const { t } = useTranslation()
  const game = useSelector(selectGame)
  const gameConfig = useSelector(selectGameConfig)
  const userId = useSelector(selectViewerId)
  const validUnits = useSelector(selectValidUnitsForPlayer)
  const loading = mode === "load"

  const users = game?.metadata?.users || {}
  const me = users[String(userId)] || {}
  const key = city ? `${city.col}-${city.row}` : null
  const here = key ? game?.metadata?.cells?.[key]?.metadata_by_user?.[String(userId)] : null

  // The map bar's order for this city: enabled or not, the reason, the entry's cap and the room
  // the network has left. What may be picked entering is the smaller of the two.
  const order = tunnelOrdersAt({ city, game, gameConfig, userId })[loading ? "tunnelLoad" : "tunnelUnload"]
  const entryCap = order?.capacity ?? 0
  const room = order?.room ?? 0
  const capacity = loading ? Math.min(entryCap, room) : null

  // What may be picked: the garrison when entering, the network when leaving — in the class's
  // order, as the march lists it.
  const rank = (type) => (validUnits.includes(type) ? validUnits.indexOf(type) : validUnits.length)
  const pool = Object.entries((loading ? here?.units : me.tunnel) || {})
    .map(([type, data]) => ({ type, available: data?.number ?? 0 }))
    .filter(({ available }) => available > 0)
    .sort((a, b) => rank(a.type) - rank(b.type))

  // Opens with everything that fits chosen, as the march opens with the whole army.
  const [troops, setTroops] = useState(() => tunnelFill({ pool, fraction: 1, capacity }))

  const units = pool.map(({ type, available }) => ({
    type,
    available,
    max: tunnelMaxFor({ type, available, troops, capacity }),
  }))
  const setUnitNumber = (type, number) =>
    setTroops((current) => {
      const available = pool.find((unit) => unit.type === type)?.available ?? 0
      const max = tunnelMaxFor({ type, available, troops: current, capacity })
      return { ...current, [type]: Math.max(0, Math.min(max, Math.floor(Number(number) || 0))) }
    })
  const fillAll = (fraction) => setTroops(tunnelFill({ pool, fraction, capacity }))

  const chosen = Object.entries(troops).filter(([, number]) => Number(number) > 0)
  const picked = chosen.reduce((sum, [, number]) => sum + Number(number), 0)
  const reason = !order
    ? playerFacingError("NoTunnelAvailable")
    : order.reason
      ? t(`game.mapAction.reason.${order.reason.key}`, order.reason)
      : null
  const blocked = Boolean(reason) || picked === 0 || (loading && picked > capacity)

  const send = () => {
    if (blocked) return
    const cell = { column: Number(city.col), row: Number(city.row) }
    const sent = wsClient.send(
      "game",
      loading
        ? { action: "tunnel_load", origin: cell, troops: Object.fromEntries(chosen) }
        : { action: "tunnel_unload", destination: cell, troops: Object.fromEntries(chosen) }
    )
    if (sent) ModalService.hide()
  }

  // Space sends, as it does from the march order: also straight after a slider or a typed count,
  // which have no use for a space. Only while this sheet is on top.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== " ") return
      if (takesText(document.activeElement)) return
      const stack = ModalService.stackTypes ?? []
      if (stack.length && stack[stack.length - 1] !== "TunnelModal") return
      event.preventDefault()
      send()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  })

  const word = t(loading ? "game.tunnel.enter" : "game.tunnel.exit")
  const name = city ? cellDisplayName({ row: city.row, column: city.col, held: true }) : ""
  const inTunnel = order?.inTunnel ?? 0
  const limit = order?.limit ?? 0

  return (
    <Modal width="min(560px, 96%)" isolated={false} scope="map" top="4vh" maxHeight="100%">
      <header className="march-head tunnel-head">
        <h1>
          🕳 {word}
          {name ? ` · ${name}` : ""}
        </h1>
        {loading && (
          <span className="tunnel-chips">
            <span
              className={`march-kind tunnel-chip${picked > entryCap ? " spent" : ""}`}
              data-testid="tunnel-capacity"
              title={t("game.tunnel.capacity", { count: entryCap })}
            >
              {picked}/{entryCap}
            </span>
            <span
              className={`march-kind tunnel-chip${room === 0 ? " spent" : ""}`}
              data-testid="tunnel-network"
              title={t("game.tunnel.network", { count: limit })}
            >
              🕳 {inTunnel}/{limit}
            </span>
          </span>
        )}
      </header>

      <UnitPicker
        className="tunnel-force"
        label={loading ? t("city.garrison") : t("game.tunnel.inTunnel")}
        units={units}
        value={troops}
        onChange={setUnitNumber}
        onFill={pool.length > 0 ? fillAll : undefined}
        emptyText={loading ? t("attack.noTroopsHere") : t("game.mapAction.reason.tunnelEmpty")}
      />

      <footer className="march-foot">
        <button className="march-send tunnel-send" data-action={loading ? "tunnel-enter" : "tunnel-exit"} onClick={send} disabled={blocked}>
          {reason || (picked === 0 ? t("attack.pickYourForce") : `🕳 ${word} · ${picked}`)}
        </button>
        <button className="quiet march-cancel" onClick={() => ModalService.hide()}>
          {t("attack.cancel")}
        </button>
      </footer>
    </Modal>
  )
}

export default TunnelModal
